import { parse } from "graphql";
import { SchemaClient } from "../../../common/GraphqlClient";
import { SchemaClientKinds, Source } from "../../../common/Source";
import { EMPTY_VALUE } from "../../../Schema";
import { uniqueSortedLabels } from "./filterValues";

/** One row of the filter-lookup table under the `responsiblePerson` partition. */
export type ResponsiblePerson = {
  filterValue: string;
};

/**
 * Responsible people, read from the same filter-lookup table as `Tags.ts` but from the
 * `responsiblePerson` partition rather than `tags`.
 *
 * GraphQL only; see {@link getResponsiblePersons}.
 */
const listResponsiblePersons: Source<ResponsiblePerson[]> = {
  graphql: parse(`
    query listIRFilterLookups($nextToken: String) {
      listIRFilterLookups(
        limit: 1000,
        nextToken: $nextToken,
        filterAttribute: "responsiblePerson",
        sortDirection: ASC
      ) {
        nextToken
        items {
          filterValue
        }
      }
    }
  `),
};

/**
 * Responsible-person filter options.
 *
 * Answers `[]` for any client that is not GraphQL. TODO: no DynamoDB request is declared yet.
 * The physical table is the one `DynamoDbTables.TAGS` already names — the lookup table
 * partitions by `filterAttribute` — so this is the cheapest of the four to complete later.
 * Safe as a stub because these values only populate a grid filter dropdown and never reach
 * `DataManager`.
 *
 * @param client - Read client for the master data.
 * @returns Responsible-person names, deduplicated and sorted.
 */
export const getResponsiblePersons = async (client: SchemaClient): Promise<string[]> => {
  if (client.kind !== SchemaClientKinds.GRAPHQL) {
    return [];
  }
  const people = await client.queryList(listResponsiblePersons);
  const items = people === EMPTY_VALUE ? [] : people ?? [];
  return uniqueSortedLabels(items.map((person) => person.filterValue));
};
